import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import { Chip, Stack } from "@mui/material";
import { useWorkerPreferences } from "@src/appV2/Agents/api/useWorkerPreferences";
import { type AgentPreferences } from "@src/appV2/Agents/api/useWorkerPreferences/types";
import { ALL_ROLES_FILTER_VALUE } from "@src/appV2/OpenShifts/components/OpenShiftCalendar/Dialogs/LicenseField";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useCallback } from "react";

import { useAllRolesFilterContext } from "../hooks/useAllRolesFilter";
import { useEnableDistanceFiltering } from "../hooks/useEnableDistanceFiltering";
import { ShiftBlockDistanceFilterDialog } from "./ShiftBlockDistanceFilterDialog";
import { ShiftLicenseBlockFilterDialog } from "./ShiftBlockLicenseFilterDialog";

function getLicenseDisplayLabel(license: string, isAllRolesSelected: boolean): string {
  if (isAllRolesSelected) {
    return "All Roles";
  }
  return license;
}

interface ShiftBlockFilterProps {
  worker: Worker;
  showDistanceFilter?: boolean;
}

export function ShiftBlockFilter(props: ShiftBlockFilterProps) {
  const { worker, showDistanceFilter = true } = props;

  const distanceFilterModalState = useModalState();
  const licenseFilterModalState = useModalState();
  const { filters, setFilters } = useWorkerPreferences();
  const { isAllRolesSelected, setIsAllRolesSelected } = useAllRolesFilterContext();

  const { enableDistanceFiltering } = useEnableDistanceFiltering();

  const handleLicenseFilterSubmit = useCallback(
    (formData: AgentPreferences) => {
      // Handle "All Roles" selection locally - don't persist to backend
      if (formData.license === ALL_ROLES_FILTER_VALUE) {
        setIsAllRolesSelected(true);
      } else {
        setIsAllRolesSelected(false);
        setFilters(formData);
      }
    },
    [setFilters, setIsAllRolesSelected]
  );

  return (
    <>
      <Stack direction="row" spacing={1}>
        {isDefined(worker.licensesData) && isDefined(filters) && isDefined(filters.license) && (
          <Chip
            color="primary"
            icon={<FilterAltOutlined />}
            aria-label="Update License"
            label={<b>{getLicenseDisplayLabel(filters.license, isAllRolesSelected)}</b>}
            onClick={() => {
              licenseFilterModalState.openModal();
            }}
          />
        )}
        {isDefined(filters) && isDefined(filters.distance) && showDistanceFilter && (
          <Chip
            color="primary"
            icon={<FilterAltOutlined />}
            aria-label="Update Distance"
            label={enableDistanceFiltering ? <b>{filters.distance} miles</b> : <b>Any distance</b>}
            onClick={() => {
              distanceFilterModalState.openModal();
            }}
          />
        )}
      </Stack>
      {isDefined(filters) && (
        <>
          <ShiftLicenseBlockFilterDialog
            modalState={licenseFilterModalState}
            agentLicenses={worker.licensesData}
            defaultValues={
              isAllRolesSelected ? { ...filters, license: ALL_ROLES_FILTER_VALUE } : filters
            }
            onSubmit={handleLicenseFilterSubmit}
          />
          <ShiftBlockDistanceFilterDialog
            modalState={distanceFilterModalState}
            defaultValues={filters}
            onSubmit={setFilters}
          />
        </>
      )}
    </>
  );
}
